import { c as _c } from "react/compiler-runtime";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationCircle } from '@fortawesome/free-solid-svg-icons';
import { AxiosError, AxiosHeaders } from 'axios';
import { helpEmail } from 'constants/global';
import { REF_KEYS_LIST } from 'constants/onboarding';
import copy from 'copy-to-clipboard';
import React from 'react';
// Not ideal but this component is imported in our api/index setup and that file is included in
// our test API mocks. Importing from @commutifi-fe/ui doesn't do any tree shaking in the test
// environment so we need to import the components directly to avoid errors on components like Mapbox.
import { Button as AppButton, ButtonSize, ButtonType } from '@commutifi-fe/ui/components/AppButton';
import { Typography as AppTypography } from '@commutifi-fe/ui/components/AppTypography';
import { message } from '@commutifi-fe/ui/components/AntdApp';
import { Divider } from '@commutifi-fe/ui/components/Divider';
import { getLanguage } from 'shared/providers/LocaleProvider/_utils';
import storage from 'utils/storage';
const globalErrorMessages: Record<string, Record<string, string>> = {
  en: {
    'error.request.failed.retrying.title': 'We detected one of our request failed. Thank you for you patience while we retry.',
    'error.request.failed.retrying.description': 'Retrying 3 times... Current attempt: ',
    'error.request.server.unreachable': 'We were not able to reach our servers. ',
    'error.request.network.offline': 'It seems you are having network issues, if you need assistance, simply copy the error and send it to us.',
    'error.request.network.online': "Copy the error to you clipboard to send in the chat or simply click 'Send email'",
    'error.request.sendEmail.subject': 'Help Request for Network issue',
    'error.request.sendEmail.body.intro': 'Hi Commutifi, can you please help me with this error I got: ',
    'global.sendEmail': 'Send Email',
    'global.copy': 'Copy',
    'global.dismiss': 'Dismiss'
  },
  fr: {
    'error.request.failed.retrying.title': "Nous avons détecté que l'une de nos requêtes a échoué. Merci de votre patience pendant que nous réessayons.",
    'error.request.failed.retrying.description': 'Nous réessayons 3 fois... Tentative actuelle :',
    'error.request.server.unreachable': "Nous n'avons pas pu joindre nos serveurs.",
    'error.request.network.offline': "Il semble que vous ayez des problèmes de réseau, si vous avez besoin d'aide, copiez simplement l'erreur et envoyez-la nous.",
    'error.request.network.online': "Copiez l'erreur dans votre presse-papiers pour l'envoyer dans le chat ou cliquez simplement sur « Envoyer un email »",
    'error.request.sendEmail.subject': "Demande d'aide pour problème de réseau",
    'error.request.sendEmail.body.intro': "Salut Commutifi, pouvez-vous s'il vous plaît m'aider avec cette erreur que j'ai: ",
    'global.sendEmail': 'Envoyer un email',
    'global.copy': 'Copier',
    'global.dismiss': 'Fermer'
  }
};
const t = (key: string) => {
  const locale = storage.get('lang') || 'en-us';
  const lang: 'en' | 'fr' = getLanguage(locale);
  const messages = globalErrorMessages[lang] || globalErrorMessages.en;
  return messages[key];
};
const errorButtonStyle = {
  padding: 0,
  height: 'auto'
};
const errorButtonCommonProps = {
  type: 'link' as ButtonType,
  size: 'small' as ButtonSize
};
export function RecurringError(t0) {
  const $ = _c(25);
  const {
    error
  } = t0;
  let t1;
  let t2;
  if ($[0] !== error) {
    const parsedData = JSON.parse(error.config?.data || "{}");
    const sanitizedConfig = {
      ...error.config,
      headers: error.config?.headers || new AxiosHeaders(),
      data: {
        ...parsedData,
        ...(parsedData.answers && {
          answers: (parsedData.answers || []).map(_temp)
        })
      }
    };
    const sanitizedError = {
      ...error.toJSON(),
      config: sanitizedConfig
    };
    if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = t("error.request.sendEmail.body.intro");
      $[3] = t1;
    } else {
      t1 = $[3];
    }
    t2 = JSON.stringify(sanitizedError);
    $[0] = error;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  const errorMessage = `${t1}${t2}`;
  let t3;
  if ($[4] !== error.config?.url) {
    t3 = () => {
      message.destroy(error.config?.url);
    };
    $[4] = error.config?.url;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const handleDestroyMessage = t3;
  let t4;
  if ($[6] !== errorMessage) {
    t4 = () => {
      copy(errorMessage);
    };
    $[6] = errorMessage;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const handleCopyToClipboard = t4;
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      display: "inline-flex",
      flexDirection: "column"
    };
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  let t7;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <AppTypography.Text className="u-margin-bottom--medium">{t("error.request.server.unreachable")}{!window.navigator.onLine ? t("error.request.network.offline") : t("error.request.network.online")}</AppTypography.Text>;
    t7 = {
      display: "inline-flex",
      justifyContent: "space-between"
    };
    $[9] = t6;
    $[10] = t7;
  } else {
    t6 = $[9];
    t7 = $[10];
  }
  let t8;
  if ($[11] !== errorMessage) {
    t8 = !window.navigator.onLine ? null : <><AppButton {...errorButtonCommonProps} href={`mailto:${helpEmail}?subject=${encodeURIComponent(t("error.request.sendEmail.subject"))}&body=${encodeURIComponent(errorMessage)}`} target="_blank" className="u-font-weight-semi" style={errorButtonStyle}>{t("global.sendEmail")}</AppButton><Divider type="vertical" className="u-margin-horizontal--small" /></>;
    $[11] = errorMessage;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = t("global.copy");
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== handleCopyToClipboard) {
    t10 = <AppButton {...errorButtonCommonProps} onClick={handleCopyToClipboard} className="u-font-weight-semi" style={errorButtonStyle}>{t9}</AppButton>;
    $[14] = handleCopyToClipboard;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== t10 || $[17] !== t8) {
    t11 = <div>{t8}{t10}</div>;
    $[16] = t10;
    $[17] = t8;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = t("global.dismiss");
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] !== handleDestroyMessage) {
    t13 = <AppButton {...errorButtonCommonProps} className="u-color--neutral-500" onClick={handleDestroyMessage} style={errorButtonStyle}>{t12}</AppButton>;
    $[20] = handleDestroyMessage;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== t11 || $[23] !== t13) {
    t14 = <span style={t5}>{t6}<span style={t7}>{t11}{t13}</span></span>;
    $[22] = t11;
    $[23] = t13;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  return t14;
}
function _temp(answer) {
  if (answer.referenceKey === REF_KEYS_LIST.accountPassword) {
    return {
      ...answer,
      value: ""
    };
  }
}
export function RetryingError(t0) {
  const $ = _c(5);
  const {
    count
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      display: "inline-flex",
      flexDirection: "column"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <AppTypography.Text strong="semi-bold">{t("error.request.failed.retrying.title")}</AppTypography.Text>;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = t("error.request.failed.retrying.description");
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== count) {
    t4 = <span className="u-margin-left--small" style={t1}>{t2}<AppTypography.Text type="secondary">{t3}{count}</AppTypography.Text></span>;
    $[3] = count;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  return t4;
}
export const displayErrorRetryLoading = (key: string, count: number) => {
  message.destroy();
  return message.loading({
    content: <RetryingError count={count} />,
    key,
    duration: 8
  });
};
export const displayFailedRetryError = (error: any) => {
  message.destroy();
  return message.error({
    key: error.config.url,
    content: <RecurringError error={error} />,
    duration: 0,
    icon: <FontAwesomeIcon icon={faExclamationCircle} className="u-margin-bottom--tiny" size="lg" />
  });
};
export const destroyMessage = (key: string) => message.destroy(key);